// Turns uploaded menu files (images or PDFs) into compressed JPEG images
// that can be sent to Claude. Claude reads the images directly, so there is
// no separate text-extraction (OCR) step.

export interface MenuImage {
  mediaType: 'image/jpeg'
  data: string // base64, without the data: prefix
}

export interface ImageOptions {
  maxEdge: number // longest side in pixels
  quality: number // JPEG quality, 0 to 1
}

export const MAX_MENU_PAGES = 8
export const DEFAULT_IMAGE_OPTIONS: ImageOptions = { maxEdge: 1600, quality: 0.85 }
export const SMALL_IMAGE_OPTIONS: ImageOptions = { maxEdge: 1200, quality: 0.7 }

function scaleToFit(width: number, height: number, maxEdge: number) {
  const scale = Math.min(1, maxEdge / Math.max(width, height))
  return { width: Math.round(width * scale), height: Math.round(height * scale) }
}

function createCanvas(width: number, height: number) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('Your browser could not prepare this file. Please try a different browser.')
  }
  // White background so transparent PNGs don't turn black as JPEGs
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, width, height)
  return { canvas, context }
}

function canvasToMenuImage(canvas: HTMLCanvasElement, quality: number): MenuImage {
  const dataUrl = canvas.toDataURL('image/jpeg', quality)
  return { mediaType: 'image/jpeg', data: dataUrl.slice(dataUrl.indexOf(',') + 1) }
}

async function imageFileToMenuImage(file: File, options: ImageOptions): Promise<MenuImage> {
  const bitmap = await createImageBitmap(file)
  try {
    const { width, height } = scaleToFit(bitmap.width, bitmap.height, options.maxEdge)
    const { canvas, context } = createCanvas(width, height)
    context.drawImage(bitmap, 0, 0, width, height)
    return canvasToMenuImage(canvas, options.quality)
  } finally {
    bitmap.close()
  }
}

async function pdfFileToMenuImages(file: File, options: ImageOptions): Promise<MenuImage[]> {
  // Loaded on demand so pdf.js isn't in the main bundle. The worker URL comes from
  // the installed package, so the worker and library versions always match.
  const [pdfjsLib, { default: workerUrl }] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ])
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl

  const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise
  if (pdf.numPages > MAX_MENU_PAGES) {
    throw new Error(
      `"${file.name}" has ${pdf.numPages} pages. Please upload ${MAX_MENU_PAGES} pages or fewer at a time.`
    )
  }

  const images: MenuImage[] = []
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    const page = await pdf.getPage(pageNumber)
    const base = page.getViewport({ scale: 1 })
    const scale = options.maxEdge / Math.max(base.width, base.height)
    const viewport = page.getViewport({ scale })
    const { canvas, context } = createCanvas(Math.round(viewport.width), Math.round(viewport.height))
    await page.render({ canvas, canvasContext: context, viewport }).promise
    images.push(canvasToMenuImage(canvas, options.quality))
  }
  return images
}

export async function prepareMenuImages(
  file: File,
  options: ImageOptions = DEFAULT_IMAGE_OPTIONS
): Promise<MenuImage[]> {
  if (file.type === 'application/pdf') {
    return pdfFileToMenuImages(file, options)
  }
  return [await imageFileToMenuImage(file, options)]
}

export function totalBase64Size(images: MenuImage[]): number {
  return images.reduce((sum, image) => sum + image.data.length, 0)
}
